import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

function LyricCard({ lyric, onUpdate }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [hasLiked, setHasLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(lyric.likes_count || 0);
  const [isLiking, setIsLiking] = useState(false);
  const [dominantColor, setDominantColor] = useState('#ffffff');

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
      
      if (user) {
        checkIfLiked(user.id);
      }
    };
    getUser();
  }, []);

  const checkIfLiked = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('lyric_likes')
        .select('id')
        .eq('lyric_id', lyric.id)
        .eq('user_id', userId)
        .maybeSingle();

      if (!error && data) {
        setHasLiked(true);
      }
    } catch (error) {
      console.error('Error verificando like:', error);
    }
  };

  const handleLike = async () => {
    if (!currentUser || isLiking) return;

    setIsLiking(true);

    try {
      if (hasLiked) {
        // Unlike
        const { error: deleteError } = await supabase
          .from('lyric_likes')
          .delete()
          .eq('lyric_id', lyric.id)
          .eq('user_id', currentUser.id);

        if (deleteError) throw deleteError;

        const { error: updateError } = await supabase
          .from('lyrics')
          .update({ likes_count: Math.max(0, likesCount - 1) })
          .eq('id', lyric.id);

        if (updateError) throw updateError;

        setHasLiked(false);
        setLikesCount(prev => Math.max(0, prev - 1));
      } else {
        // Like
        const { error: insertError } = await supabase
          .from('lyric_likes')
          .insert({
            lyric_id: lyric.id,
            user_id: currentUser.id,
          });

        if (insertError) throw insertError;

        const { error: updateError } = await supabase
          .from('lyrics')
          .update({ likes_count: likesCount + 1 })
          .eq('id', lyric.id);

        if (updateError) throw updateError;

        setHasLiked(true);
        setLikesCount(prev => prev + 1);
      }

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error al dar like:', error);
      alert('Error al dar like. Intenta de nuevo.');
    } finally {
      setIsLiking(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'hace ' + diffInSeconds + 's';
    if (diffInSeconds < 3600) return 'hace ' + Math.floor(diffInSeconds / 60) + 'm';
    if (diffInSeconds < 86400) return 'hace ' + Math.floor(diffInSeconds / 3600) + 'h';
    if (diffInSeconds < 2592000) return 'hace ' + Math.floor(diffInSeconds / 86400) + 'd';
    return 'hace ' + Math.floor(diffInSeconds / 2592000) + ' meses';
  };

  const extractDominantColor = (imageUrl) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = imageUrl;

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        let r = 0, g = 0, b = 0;
        const pixelCount = data.length / 4;

        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
        }

        r = Math.floor(r / pixelCount);
        g = Math.floor(g / pixelCount);
        b = Math.floor(b / pixelCount);

        // Mantener colores fuertes con mínimo suavizado
        const softenFactor = 0.15;
        r = Math.floor(r + (255 - r) * softenFactor);
        g = Math.floor(g + (255 - g) * softenFactor);
        b = Math.floor(b + (255 - b) * softenFactor);

        setDominantColor(`rgb(${r}, ${g}, ${b})`);
      } catch (error) {
        console.error('Error extrayendo color:', error);
        setDominantColor('#ffffff');
      }
    };

    img.onerror = () => {
      setDominantColor('#ffffff');
    };
  };

  useEffect(() => {
    const track = lyric.tracks;
    if (track?.cover_image) {
      extractDominantColor(track.cover_image);
    }
  }, [lyric]);

  const user = lyric.users;
  const track = lyric.tracks;

  return (
    <div style={{...styles.card, backgroundColor: dominantColor}}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.userInfo}>
          {user?.avatar_url && user.avatar_url.trim() !== '' ? (
            <img
              src={user.avatar_url}
              alt={user.username || 'Usuario'}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
                border: '2px solid #1e3a8a'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = document.createElement('div');
                Object.assign(fallback.style, styles.avatar);
                fallback.textContent = user?.username?.[0]?.toUpperCase() || 'U';
                e.target.parentElement.insertBefore(fallback, e.target);
              }}
            />
          ) : (
            <div style={styles.avatar}>
              {user?.username?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <div style={styles.userDetails}>
            <h3 style={styles.userName}>@{user?.username || 'user'}</h3>
          </div>
        </div>
        <span style={styles.timeAgo}>{formatTimeAgo(lyric.created_at)}</span>
      </div>

      {/* Track Info */}
      <div style={styles.trackInfo}>
        <img
          src={track?.cover_image || 'https://via.placeholder.com/60?text=No+Cover'}
          alt={track?.title}
          style={styles.coverImage}
        />
        <div style={styles.trackDetails}>
          <h4 style={styles.trackTitle}>{track?.title || 'Canción desconocida'}</h4>
          <p style={styles.trackArtist}>{track?.artist || 'Artista desconocido'}</p>
        </div>
      </div>

      {/* Lyric Content */}
      <div style={styles.lyricContent}>
        <p style={styles.lyricText}>{lyric.content}</p>
      </div>

      {/* Like Button */}
      <button
        onClick={handleLike}
        disabled={!currentUser || isLiking}
        style={{
          ...styles.likeButton,
          ...(hasLiked ? styles.likeButtonActive : {}),
          ...(!currentUser || isLiking ? styles.likeButtonDisabled : {})
        }}
      >
        <span style={styles.likeIcon}>❤️</span>
        <span>{likesCount}</span>
      </button>
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s',
    height: '500px',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
  },
  userInfo: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#1e3a8a',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  userName: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '600',
    color: '#222',
  },
  timeAgo: {
    fontSize: '13px',
    color: '#666',
    fontWeight: '500',
  },
  trackInfo: {
    display: 'flex',
    gap: '12px',
    padding: '10px',
    backgroundColor: 'transparent',
    borderRadius: '8px',
    marginBottom: '15px',
  },
  coverImage: {
    width: '60px',
    height: '60px',
    borderRadius: '6px',
    objectFit: 'cover',
    flexShrink: 0,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
  },
  trackDetails: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '4px',
  },
  trackTitle: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '700',
    color: '#222',
  },
  trackArtist: {
    margin: 0,
    fontSize: '14px',
    color: '#555',
    fontWeight: '500',
  },
  lyricContent: {
    backgroundColor: 'transparent',
    borderRadius: '8px',
    padding: '18px',
    marginBottom: '15px',
    flex: 1,
    overflow: 'auto',
    maxHeight: '280px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  lyricText: {
    margin: 0,
    fontSize: '18px',
    lineHeight: '1.7',
    color: '#1a1a1a',
    fontWeight: '600',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    textAlign: 'left',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    width: 'fit-content',
    maxWidth: '100%',
  },
  likeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    border: 'none',
    borderRadius: '20px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    alignSelf: 'flex-start',
    marginTop: 'auto',
  },
  likeButtonActive: {
    backgroundColor: '#ffe0e6',
    color: '#d91b5c',
  },
  likeButtonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  likeIcon: {
    fontSize: '18px',
  },
};

export default LyricCard;